import React, { useMemo } from 'react';
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from '@carbon/react';
import { monthlyVolumeToDaily } from 'components/well-page-content/decline-curve-analysis/volume-display-scaling';
import { DcaForecastParameter, DcaForecastParams } from 'types/api-responses';
import type { MineralType } from 'types/minerals';
import { formatNumber } from 'utils/number';
import styles from './dca-parameters-table.module.scss';

type DcaRow = {
  id: string;
  metric: string;
  oil: string;
  gas: string;
  water: string;
};

type FluidType = 'oil' | 'gas' | 'water';

type DcaFieldDef = {
  id: string;
  label: string;
  getUnit: (type: FluidType) => string;
  decimals: number;
  getValue: (params: DcaForecastParams | null) => number | null;
};

const DCA_FIELDS: DcaFieldDef[] = [
  {
    id: 'ramp_up_qi',
    label: 'Ramp Up qi',
    getUnit: (type) => (type === 'gas' ? 'mcf' : 'bbl'),
    decimals: 0,
    getValue: (params) => params?.ramp_up?.qi ?? null,
  },
  {
    id: 'ramp_up_qi_daily',
    label: 'Ramp Up qi (daily)',
    getUnit: (type) => (type === 'gas' ? 'mcf/day' : 'bbl/day'),
    decimals: 0,
    getValue: (params) => {
      const qi = params?.ramp_up?.qi;
      return typeof qi === 'number' ? monthlyVolumeToDaily(qi) : null;
    },
  },
  {
    id: 'ramp_up_slope',
    label: 'Ramp Up Slope',
    getUnit: () => 'bbl/month^2',
    decimals: 0,
    getValue: (params) => params?.ramp_up?.n ?? null,
  },
  {
    id: 'ramp_up_months',
    label: 'Ramp-up Duration',
    getUnit: () => 'months',
    decimals: 0,
    getValue: (params) => params?.ramp_up?.t_ramp ?? null,
  },
  {
    id: 'qi',
    label: 'qi',
    getUnit: (type) => (type === 'gas' ? 'mcf/month' : 'bbl/month'),
    decimals: 0,
    getValue: (params) => params?.qi ?? null,
  },
  {
    id: 'qi_daily',
    label: 'qi (daily)',
    getUnit: (type) => (type === 'gas' ? 'mcf/day' : 'bbl/day'),
    decimals: 0,
    getValue: (params) => {
      return typeof params?.qi === 'number'
        ? monthlyVolumeToDaily(params.qi)
        : null;
    },
  },
  {
    id: 'di',
    label: 'Di',
    getUnit: () => '%/year',
    decimals: 1,
    getValue: (params) => params?.di ?? null,
  },
  {
    id: 'b_factor',
    label: 'b-factor',
    getUnit: () => '',
    decimals: 2,
    getValue: (params) => params?.b_factor ?? null,
  },
  {
    id: 'dlim',
    label: 'Dlim',
    getUnit: () => '%/year',
    decimals: 1,
    getValue: (params) => params?.dlim ?? null,
  },
];

const fmtDca = (
  value: number | null | undefined,
  decimals: number,
  unit: string
): string => {
  if (value === null || value === undefined) return '--';
  const formatted = formatNumber(
    value,
    decimals,
    decimals,
    'decimal',
    true,
    '--'
  );
  return unit ? `${formatted} ${unit}` : formatted;
};

const columns: ColumnDef<DcaRow>[] = [
  { id: 'metric', accessorKey: 'metric', header: '' },
  { id: 'oil', accessorKey: 'oil', header: 'Oil' },
  { id: 'gas', accessorKey: 'gas', header: 'Gas' },
  { id: 'water', accessorKey: 'water', header: 'Water' },
];

export const DcaParametersTable: React.FC<{
  dcaForecastParameters: DcaForecastParameter[];
  dcaFitParams?: Partial<Record<MineralType, DcaForecastParams>>;
}> = ({ dcaForecastParameters, dcaFitParams }) => {
  const oilParams =
    dcaFitParams?.oil ??
    dcaForecastParameters.find((p) => p.stream === 'oil')?.dcaParams ??
    null;
  const gasParams =
    dcaFitParams?.gas ??
    dcaForecastParameters.find((p) => p.stream === 'gas')?.dcaParams ??
    null;
  const waterParams =
    dcaFitParams?.water ??
    dcaForecastParameters.find((p) => p.stream === 'water')?.dcaParams ??
    null;

  const data: DcaRow[] = useMemo(() => {
    return DCA_FIELDS.map((def) => ({
      id: def.id,
      metric: def.label,
      oil: fmtDca(def.getValue(oilParams), def.decimals, def.getUnit('oil')),
      gas: fmtDca(def.getValue(gasParams), def.decimals, def.getUnit('gas')),
      water: fmtDca(
        def.getValue(waterParams),
        def.decimals,
        def.getUnit('water')
      ),
    }));
  }, [oilParams, gasParams, waterParams]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>DCA Parameters</h3>
      <Table size="sm">
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeader key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHeader>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell, idx) => (
                <TableCell
                  key={cell.id}
                  className={idx === 0 ? styles.metricCell : undefined}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
